import React, { Component } from 'react';
import PropTypes from 'prop-types';
import keyBy from 'lodash/keyBy';
import { FaExclamation as WarningIcon } from 'react-icons/fa';

import { Checkbox, CheckboxGroup } from './react-checkbox-group';

import withPillNavigation from '../../shared/withPillNavigation.decorator';
import AgGrid from '../../shared/agGrid/agGrid.component';
import {
  singleAssignmentPropTypes,
  userClientPropTypes,
} from '../../shared/propTypes/userClients';
import {
  availablePermissionsPropTypes,
  clientRolePropTypes,
  userSettingsFormDataPropTypesForClient,
} from '../propTypes';

import AssignmentsSelect from './assignmentsSelect.component';
import getColumnDefs from '../../shared/columnDefinitions/companyAssignments';
import { userRoles } from '../../shared/authorization';
import { assignmentTypes, assignmentTypes as assignmentShortTypes } from '../../shared/authorization/assignmentTypes';
import { getPermissionsByRoleAndClientSelector as getPermissionsFunction } from '../addEditUserSettingsForm.helpers';

import { filterAssignmentsByType } from '../../utils/permissionsHelpers';
import { ALL_ASSIGNMENTS_CODE } from '../constants';
import { uniq } from '../../shared/uniq';
import { getAssignmentKey } from '../utils';
import { debouncedRebuildReactTooltip } from '../../shared/debouncedRebuildReactTooltip';

const AssignmentTypeNames = {
  COUNTRY: 'Country',
  CONNECTION: 'Connection',
  BUSINESS_UNIT: 'Business Unit',
  COMPANY_CODE: 'Company Code',
  BUSINESS: 'Business',
  ALL: 'All',
};

const AssignmentTypeNamesByShortType = {
  [assignmentTypes.COUNTRIES]: AssignmentTypeNames.COUNTRY,
  [assignmentTypes.CONNECTIONS]: AssignmentTypeNames.CONNECTION,
  [assignmentTypes.BUSINESS_UNITS]: AssignmentTypeNames.BUSINESS_UNIT,
  [assignmentTypes.COMPANY_CODES]: AssignmentTypeNames.COMPANY_CODE,
  [assignmentTypes.BUSINESSES]: AssignmentTypeNames.BUSINESS,
  [assignmentTypes.ALL]: AssignmentTypeNames.ALL,
};


const assignmentTypesOrder = [
  assignmentTypes.BUSINESSES,
  assignmentTypes.COUNTRIES,
  assignmentTypes.CONNECTIONS,
  assignmentTypes.BUSINESS_UNITS,
  assignmentTypes.COMPANY_CODES,
].reduce((order, assignmentType, index) => ({
  ...order,
  [assignmentType]: index,
}), {});

const currentAssignmentComparator = (a, b) => (
  assignmentTypesOrder[a.assignmentTypeCode]
  - assignmentTypesOrder[b.assignmentTypeCode]
)
    || a.value.localeCompare(b.value);

export const PillTypes = {
  CURRENT_ASSIGNMENTS: 'CURRENT_ASSIGNMENTS',
  BUSINESSES: 'BUSINESSES',
  COUNTRIES: 'COUNTRIES',
  CONNECTIONS: 'CONNECTIONS',
  BUSINESS_UNITS: 'BUSINESS_UNITS',
  COMPANY_CODES: 'COMPANY_CODES',
  ALL: 'ALL',
};

const PillsAssignmentShortTypes = {
  [PillTypes.BUSINESS_UNITS]: assignmentShortTypes.BUSINESS_UNITS,
  [PillTypes.COMPANY_CODES]: assignmentShortTypes.COMPANY_CODES,
  [PillTypes.CONNECTIONS]: assignmentShortTypes.CONNECTIONS,
  [PillTypes.COUNTRIES]: assignmentShortTypes.COUNTRIES,
  [PillTypes.BUSINESSES]: assignmentShortTypes.BUSINESSES,
  [PillTypes.ALL]: assignmentShortTypes.ALL,
};

const AssignmentShortTypesValueFields = {
  [assignmentShortTypes.BUSINESS_UNITS]: 'businessUnit',
  [assignmentShortTypes.COMPANY_CODES]: 'companyCode',
  [assignmentShortTypes.CONNECTIONS]: 'connectionName',
  [assignmentShortTypes.COUNTRIES]: 'countryCode',
  [assignmentShortTypes.BUSINESSES]: 'businessCode',
  [assignmentShortTypes.ALL]: 'allCode',
};

const PillsDefinition = [
  { label: 'Current Assignments', type: PillTypes.CURRENT_ASSIGNMENTS },
  { label: 'Businesses', type: PillTypes.BUSINESSES },
  { label: 'Countries', type: PillTypes.COUNTRIES },
  { label: 'Connections', type: PillTypes.CONNECTIONS },
  { label: 'Business Units', type: PillTypes.BUSINESS_UNITS },
  { label: 'Company Codes', type: PillTypes.COMPANY_CODES },
  { label: 'All', type: PillTypes.ALL },
];

const allCollectionForLastPill = [{
  allCode: ALL_ASSIGNMENTS_CODE,
  allDescription: 'All',
}];


const getCurrentAssignmentsRowData = ({
  countries,
  connections,
  businessUnits,
  companyCodes,
  businesses,
}, roleAssignmentsMap) => ([
  ...allCollectionForLastPill.map(({ allCode }) => ({
    assignmentType: AssignmentTypeNames.ALL,
    assignmentTypeCode: assignmentShortTypes.ALL,
    value: allCode,
    label: allCode,
    checked: true,
    fromRole: Boolean(roleAssignmentsMap[getAssignmentKey({
      assignmentType: assignmentShortTypes.ALL,
      assignmentCode: allCode,
    })]),
  })),
  ...countries.map(({ countryCode }) => ({
    assignmentType: AssignmentTypeNames.COUNTRY,
    assignmentTypeCode: assignmentShortTypes.COUNTRIES,
    value: countryCode,
    label: countryCode,
    checked: true,
    fromRole: Boolean(roleAssignmentsMap[getAssignmentKey({
      assignmentType: assignmentShortTypes.ALL,
      assignmentCode: countryCode,
    })]),
  })),
  ...connections.map(({ connectionName }) => ({
    assignmentType: AssignmentTypeNames.CONNECTION,
    assignmentTypeCode: assignmentShortTypes.CONNECTIONS,
    value: connectionName,
    label: connectionName,
    checked: true,
    fromRole: Boolean(roleAssignmentsMap[getAssignmentKey({
      assignmentType: assignmentShortTypes.CONNECTIONS,
      assignmentCode: connectionName,
    })]),
  })),
  ...businessUnits.map(({ businessUnit }) => ({
    assignmentType: AssignmentTypeNames.BUSINESS_UNIT,
    assignmentTypeCode: assignmentShortTypes.BUSINESS_UNITS,
    value: businessUnit,
    label: businessUnit,
    checked: true,
    fromRole: Boolean(roleAssignmentsMap[getAssignmentKey({
      assignmentType: assignmentShortTypes.BUSINESS_UNITS,
      assignmentCode: businessUnit,
    })]),
  })),
  ...companyCodes.map(({ companyCode }) => ({
    assignmentType: AssignmentTypeNames.COMPANY_CODE,
    assignmentTypeCode: assignmentShortTypes.COMPANY_CODES,
    value: companyCode,
    label: companyCode,
    checked: true,
    fromRole: Boolean(roleAssignmentsMap[getAssignmentKey({
      assignmentType: assignmentShortTypes.COMPANY_CODES,
      assignmentCode: companyCode,
    })]),
  })),
  ...businesses.map(({ businessCode, businessDescription }) => ({
    assignmentType: AssignmentTypeNames.BUSINESS,
    assignmentTypeCode: assignmentShortTypes.BUSINESSES,
    value: businessCode,
    label: businessDescription,
    checked: true,
    fromRole: Boolean(roleAssignmentsMap[getAssignmentKey({
      assignmentType: assignmentShortTypes.BUSINESSES,
      assignmentCode: businessCode,
    })]),
  })),
]);

const filterAssignmentPermissionsByPrivilege = (
  loggedInUserCheckedAssignments,
  assignmentCode,
  availableAssignmentPermissions
) => {
  const foundAssignment = loggedInUserCheckedAssignments.find(
    ({ assignmentCode: assignment }) => assignment === assignmentCode || assignment === ALL_ASSIGNMENTS_CODE
  );
  return foundAssignment
    ? availableAssignmentPermissions.filter(permission => foundAssignment.privileges.includes(permission.value))
    : [];
};

const getRowData = (
  pillType,
  userRole,
  rowData,
  checkedAssignmentsMap,
  currentRoleAssignmentsMap,
  allChecked = false
) => {
  const assignmentShortType = PillsAssignmentShortTypes[pillType];
  const valueFieldName = AssignmentShortTypesValueFields[assignmentShortType];

  const mapRowData = row => {
    const value = row[valueFieldName];
    const checked = Boolean(checkedAssignmentsMap?.[assignmentShortType]?.[value]);

    const fromRole = Boolean(currentRoleAssignmentsMap?.[getAssignmentKey({
      assignmentType: assignmentShortType,
      assignmentCode: value,
    })]);

    return {
      ...row,
      assignmentTypeCode: assignmentShortType,
      value,
      checked,
      fromRole,
    };
  };

  const SelectAllAdminRows = {
    [PillTypes.BUSINESS_UNITS]: {
      businessUnit: 'All',
      businessUnitDescription: '',
      checked: allChecked,
    },
    [PillTypes.COMPANY_CODES]: {
      companyCode: 'All',
      companyCodeDescription: '',
      checked: allChecked,
    },
    [PillTypes.CONNECTIONS]: {
      connectionName: 'All',
      checked: allChecked,
    },
    [PillTypes.COUNTRIES]: {
      countryCode: 'All',
      countryDescription: 'All',
      checked: allChecked,
    },
    [PillTypes.BUSINESSES]: {
      businessCode: 'All',
      businessDescription: 'All',
      checked: allChecked,
    },
  };

  if (!rowData.length || pillType === PillTypes.ALL) {
    return rowData.map(mapRowData);
  }

  return [SelectAllAdminRows[pillType]].concat(rowData.map(mapRowData));
};

const getConcatenatedUniqueCheckboxValues = assignments => assignments.length
  ? uniq(assignments.flatMap(({ privileges }) => privileges))
  : [];

const getMissingDependencies = (assignmentPermission, enabledPermissions, rolePermissions ) =>
  assignmentPermission.requiresPermissions?.filter(required =>
    !(enabledPermissions.includes(required) || rolePermissions?.includes(required))) ?? [];

class CompanyAssignments extends Component {
  static propTypes = {
    activePill: PropTypes.string.isRequired,
    defaultClientRole: clientRolePropTypes,
    userSettingsFormData: userSettingsFormDataPropTypesForClient.isRequired,
    loggedInUserCheckedAssignments: PropTypes.arrayOf(singleAssignmentPropTypes).isRequired,
    checkedAssignments: PropTypes.arrayOf(singleAssignmentPropTypes).isRequired,
    checkAssignment: PropTypes.func.isRequired,
    checkAllAssignments: PropTypes.func.isRequired,
    currentRoleAssignmentsMap: PropTypes.shape({
      USR: PropTypes.shape({
        privileges: PropTypes.arrayOf(PropTypes.string),
      }),
    }),
    changeAssignmentsPermissions: PropTypes.func.isRequired,
    changeCompanyAssignmentsPill: PropTypes.func.isRequired,
    readOnly: PropTypes.bool.isRequired,
    userRole: PropTypes.string.isRequired,
    selectedClient: userClientPropTypes,
    showAllCheckbox: PropTypes.bool.isRequired,
    isSelectedClientChecked: PropTypes.bool.isRequired,
    isVisible: PropTypes.bool.isRequired,
    availablePermissions: availablePermissionsPropTypes.isRequired,
    selectedRole: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }),
    selectedPermissionsFormValue: PropTypes.arrayOf(PropTypes.string).isRequired,
  };

  static defaultProps = {
    currentRoleAssignmentsMap: {},
  };

  state = {
    selectedAssignments: [],
    selectedAssignmentTypeCode: '',
  };

  componentDidMount() {
    debouncedRebuildReactTooltip();
  }

  shouldComponentUpdate(nextProps) {
    if (!nextProps.isVisible) {
      return false;
    }

    return true;
  }

  componentDidUpdate(prevProps) {
    if (this.gridApi) {
      this.gridApi.sizeColumnsToFit();
    }

    const pillChanged = prevProps.activePill !== this.props.activePill;
    const selectedClientChanged = prevProps.selectedClient !== this.props.selectedClient;
    if (pillChanged || selectedClientChanged) {
      this.resetSelectedAssignments();
    }

    if (
      (prevProps.readOnly && !this.props.readOnly)
      || (!prevProps.readOnly && this.props.readOnly)
    ) {
      this.preserveSelectionDuringUpdate(() => {
        this.gridApi?.redrawRows();
      });
    }

    // Only maintain selection state for assignments grid without manual styling
    if (this.state.selectedAssignments.length > 0 && this.props.activePill === PillTypes.CURRENT_ASSIGNMENTS) {
      setTimeout(() => {
        if (this.gridApi) {
          const selectedNodes = this.gridApi.getSelectedNodes();
          selectedNodes.forEach(node => {
            // CSS will handle the yellow background automatically
            node.setSelected(true, false);
          });
        }
      }, 50);
    }

    debouncedRebuildReactTooltip();
  }

  resetSelectedAssignments = () => {
    this.setState({
      selectedAssignments: [],
      selectedAssignmentTypeCode: '',
    });
  };

  preserveSelectionDuringUpdate = updateFn => {
    if (!this.gridApi) {
      updateFn();
      return;
    }

    // Store current selection
    const selectedNodes = this.gridApi.getSelectedNodes();
    const selectedRowIds = selectedNodes.map(node => node.data.value);

    // Execute the update
    updateFn();

    // Restore selection after a brief delay to allow grid to update
    if (selectedRowIds.length > 0) {
      setTimeout(() => {
        this.gridApi.forEachNode(node => {
          if (selectedRowIds.includes(node.data.value)) {
            node.setSelected(true, false);
          }
        });
      }, 100);
    }
  };

  onGridReady = params => {
    this.gridApi = params.api;
  };

  checkAssignment = (value, checked, type, source) => {
    this.props.checkAssignment(value, checked, type, source);
  };

  checkAll = (checked, type) => {
    if (!checked) {
      return this.props.checkAllAssignments([], checked, type);
    }

    const { userSettingsFormData, checkedAssignments } = this.props;

    const alreadyCheckedAssignmentsFilter = assignment => checkedAssignments.every(
      ({ assignmentCode }) => assignmentCode !== assignment
    );

    let values = [];
    if (type === assignmentShortTypes.COUNTRIES) {
      values = userSettingsFormData.countries
        .map(({ countryCode }) => countryCode)
        .filter(alreadyCheckedAssignmentsFilter);
    }
    if (type === assignmentShortTypes.CONNECTIONS) {
      values = userSettingsFormData.connections
        .map(({ connectionName }) => connectionName)
        .filter(alreadyCheckedAssignmentsFilter);
    }
    if (type === assignmentShortTypes.COMPANY_CODES) {
      values = userSettingsFormData.companyCodes
        .map(({ companyCode }) => companyCode)
        .filter(alreadyCheckedAssignmentsFilter);
    }
    if (type === assignmentShortTypes.BUSINESS_UNITS) {
      values = userSettingsFormData.businessUnits
        .map(({ businessUnit }) => businessUnit)
        .filter(alreadyCheckedAssignmentsFilter);
    }
    if (type === assignmentShortTypes.BUSINESSES) {
      values = userSettingsFormData.businesses
        .map(({ businessCode }) => businessCode)
        .filter(alreadyCheckedAssignmentsFilter);
    }

    this.props.checkAllAssignments(values, checked, type);
  };

  selectAssignments = selectedAssignments => {
    selectedAssignments.forEach(
      ({
        value,
        assignmentTypeCode,
      }) => this.checkAssignment(value, true, assignmentTypeCode)
    );

    this.props.changeCompanyAssignmentsPill(
      PillTypes.CURRENT_ASSIGNMENTS,
      () => this.highlightSelectedRows(selectedAssignments)
    );
  };

  highlightSelectedRows = selectedAssignments => {
    if (!this.gridApi) {
      return;
    }

    // Clear all current selections first
    this.gridApi.deselectAll();

    // Then select the specified rows (CSS will handle yellow background)
    this.gridApi.forEachNode(node => {
      const shouldBeSelected = selectedAssignments.some(
        selectedAssignment => selectedAssignment.value === node.data.value
      );

      if (shouldBeSelected) {
        node.setSelected(true, false);
      }
    });
  };

  onRowSelected = event => {
    // Note: Removed clearFocusedCell() to maintain persistent row highlighting
    // CSS now handles the yellow background automatically through .ag-row-selected

    const selectedNodes = event.api.getSelectedNodes();

    if (!selectedNodes[0]) {
      this.resetSelectedAssignments();
      return;
    }

    if (!event.node.isSelected()) {
      this.setState(({ selectedAssignments }) => ({
        selectedAssignments: selectedAssignments.filter(({ assignmentCode, assignmentType }) => (
          assignmentCode !== event.node.data.value
            || assignmentType !== event.node.data.assignmentTypeCode
        )),
      }));
      return;
    }

    const { assignmentTypeCode: currentAssignmentTypeCode } = event.node.data;

    // For Current Assignments tab, ensure only one assignment type is selected at a time
    if (this.props.activePill === PillTypes.CURRENT_ASSIGNMENTS
      && this.state.selectedAssignmentTypeCode !== currentAssignmentTypeCode) {
      selectedNodes.forEach(node => {
        if (node.data.assignmentTypeCode !== currentAssignmentTypeCode) {
          node.setSelected(false);
        }
      });
    }

    const selectedAssignments = selectedNodes
      .filter(selectedNode => selectedNode.data.assignmentTypeCode === currentAssignmentTypeCode)
      .map(({ data }) => ({ assignmentCode: data.value, assignmentType: data.assignmentTypeCode }));

    this.setState({
      selectedAssignments,
      selectedAssignmentTypeCode: currentAssignmentTypeCode,
    });
  };


  changeAssignmentsPermissions = checkedPrivileges => {
    const { userSettingsFormData, checkedAssignments, currentRoleAssignmentsMap } = this.props;
    const { selectedAssignments } = this.state;

    const foundSelectedAssignmentsInCheckedAssignments = checkedAssignments.filter(
      ({ assignmentCode, assignmentType }) => selectedAssignments.find(selectedAssignment => (
        selectedAssignment.assignmentCode === assignmentCode
          && selectedAssignment.assignmentType === assignmentType
      ))
    );
    const concatenatedUniqueCheckBoxValues = getConcatenatedUniqueCheckboxValues(
      foundSelectedAssignmentsInCheckedAssignments
    );

    const newValues = checkedPrivileges.filter(privilege => !concatenatedUniqueCheckBoxValues.includes(privilege));
    const removedValues = concatenatedUniqueCheckBoxValues.filter(privilege => !checkedPrivileges.includes(privilege));

    const { assignmentTypeCode } = getCurrentAssignmentsRowData(userSettingsFormData, currentRoleAssignmentsMap)
      .find(({ value, assignmentTypeCode }) => selectedAssignments.find(selectedAssignment => (
        selectedAssignment.assignmentCode === value && selectedAssignment.assignmentType === assignmentTypeCode
      )));

    this.props.changeAssignmentsPermissions({
      selectedAssignments: selectedAssignments.map(({ assignmentCode }) => assignmentCode),
      assignmentTypeCode,
      newValues,
      removedValues,
    });
  };

  render() {
    const { selectedAssignments } = this.state;
    const {
      activePill,
      userSettingsFormData,
      checkedAssignments,
      readOnly,
      userRole,
      currentRoleAssignmentsMap,
      loggedInUserCheckedAssignments,
      showAllCheckbox,
      isSelectedClientChecked,
      availablePermissions,
      selectedRole,
      selectedClient,
      selectedPermissionsFormValue,
    } = this.props;

    const checkedBusinesses = checkedAssignments.filter(filterAssignmentsByType(assignmentShortTypes.BUSINESSES));
    const checkedCountryCodes = checkedAssignments.filter(filterAssignmentsByType(assignmentShortTypes.COUNTRIES));
    const checkedConnections = checkedAssignments.filter(filterAssignmentsByType(assignmentShortTypes.CONNECTIONS));
    const checkedBusinessUnits = checkedAssignments.filter(
      filterAssignmentsByType(assignmentShortTypes.BUSINESS_UNITS)
    );
    const checkedCompanyCodes = checkedAssignments.filter(filterAssignmentsByType(assignmentShortTypes.COMPANY_CODES));
    const checkedAll = checkedAssignments.filter(filterAssignmentsByType(assignmentShortTypes.ALL));

    const allBusinessesChecked = Boolean(
      checkedBusinesses.length && checkedBusinesses.length === userSettingsFormData.businesses.length
    );
    const allCountryCodesChecked = Boolean(
      checkedCountryCodes.length && checkedCountryCodes.length === userSettingsFormData.countries.length
    );
    const allConnectionsChecked = Boolean(
      checkedConnections.length && checkedConnections.length === userSettingsFormData.connections.length
    );
    const allBusinessUnitsChecked = Boolean(
      checkedBusinessUnits.length && checkedBusinessUnits.length === userSettingsFormData.businessUnits.length
    );
    const allCompanyCodesChecked = Boolean(
      checkedCompanyCodes.length && checkedCompanyCodes.length === userSettingsFormData.companyCodes.length
    );

    const columnDefs = getColumnDefs({
      checkedAssignments,
      checkedBusinesses,
      checkedCountryCodes,
      checkedConnections,
      checkedBusinessUnits,
      checkedCompanyCodes,
      checkedAll,

      allBusinessesChecked,
      allCountryCodesChecked,
      allConnectionsChecked,
      allBusinessUnitsChecked,
      allCompanyCodesChecked,

      checkAssignment: this.checkAssignment,
      checkAll: this.checkAll,
      readOnly,
    });

    const currentAssignmentsRowData = getCurrentAssignmentsRowData(
      userSettingsFormData,
      currentRoleAssignmentsMap
    );

    const checkedAssignmentsMap = checkedAssignments.reduce((accu, assignment) => {
      if (!accu[assignment.assignmentType]) {
        accu[assignment.assignmentType] = {};
      }
      accu[assignment.assignmentType][assignment.assignmentCode] = assignment;
      return accu;
    }, {});

    const currentAssignmentsRowDataFiltered = currentAssignmentsRowData.filter(
      ({ assignmentTypeCode, value }) => checkedAssignmentsMap?.[assignmentTypeCode]?.[value]
    );

    const currentRoleAssignments = Object.values(currentRoleAssignmentsMap)
      .reduce((assignments, { assignmentType, assignmentCode }) => {
        if (checkedAssignmentsMap?.[assignmentType]?.[assignmentCode]) {
          return assignments;
        }

        if (assignmentType === assignmentTypes.USER) {
          return assignments;
        }

        assignments.push({
          assignmentType: AssignmentTypeNamesByShortType[assignmentType],
          assignmentTypeCode: assignmentType,
          value: assignmentCode,
          label: assignmentCode,
          checked: true,
          fromRole: true,
        });

        return assignments;
      }, []);


    const agGridConfigs = {
      [PillTypes.CURRENT_ASSIGNMENTS]: {
        columnDefs: columnDefs.currentAssignmentsColumnDefs,
        rowData: [
          ...currentRoleAssignments,
          ...currentAssignmentsRowDataFiltered,
        ].sort(currentAssignmentComparator),
      },
      [PillTypes.BUSINESSES]: {
        columnDefs: columnDefs.businessColumnDefs,
        rowData: !isSelectedClientChecked
          ? []
          : getRowData(
            PillTypes.BUSINESSES,
            userRole,
            userSettingsFormData.businesses,
            checkedAssignmentsMap,
            currentRoleAssignmentsMap ?? {},
            allBusinessesChecked
          ),
      },
      [PillTypes.COUNTRIES]: {
        columnDefs: columnDefs.countriesColumnDefs,
        rowData: !isSelectedClientChecked
          ? []
          : getRowData(
            PillTypes.COUNTRIES,
            userRole,
            userSettingsFormData.countries,
            checkedAssignmentsMap,
            currentRoleAssignmentsMap,
            allCountryCodesChecked
          ),
      },
      [PillTypes.CONNECTIONS]: {
        columnDefs: columnDefs.connectionsColumnDefs,
        rowData: !isSelectedClientChecked
          ? []
          : getRowData(
            PillTypes.CONNECTIONS,
            userRole,
            userSettingsFormData.connections,
            checkedAssignmentsMap,
            currentRoleAssignmentsMap,
            allConnectionsChecked
          ),
      },
      [PillTypes.BUSINESS_UNITS]: {
        columnDefs: columnDefs.businessUnitsColumnDefs,
        rowData: !isSelectedClientChecked
          ? []
          : getRowData(
            PillTypes.BUSINESS_UNITS,
            userRole,
            userSettingsFormData.businessUnits,
            checkedAssignmentsMap,
            currentRoleAssignmentsMap,
            allBusinessUnitsChecked
          ),
      },
      [PillTypes.COMPANY_CODES]: {
        columnDefs: columnDefs.companyCodesColumnDefs,
        rowData: !isSelectedClientChecked
          ? []
          : getRowData(
            PillTypes.COMPANY_CODES,
            userRole,
            userSettingsFormData.companyCodes,
            checkedAssignmentsMap,
            currentRoleAssignmentsMap,
            allCompanyCodesChecked
          ),
      },
      [PillTypes.ALL]: {
        columnDefs: columnDefs.allDataTypeColumnDefs,
        rowData: !isSelectedClientChecked
          ? []
          : getRowData(
            PillTypes.ALL,
            userRole,
            showAllCheckbox ? allCollectionForLastPill : [],
            checkedAssignmentsMap,
            currentRoleAssignmentsMap
          ),
      },
    };

    const foundSelectedAssignmentsInCheckedAssignments = checkedAssignments.filter(
      ({ assignmentCode, assignmentType }) => (selectedAssignments.find(selectedAssignment => (
        selectedAssignment.assignmentCode === assignmentCode
          && selectedAssignment.assignmentType === assignmentType
      )))
    );

    const foundSelectedAssignmentsInCheckedRoleAssignments = Object.values(currentRoleAssignmentsMap)
      .filter(roleAssignment =>
        (selectedAssignments.find(selectedAssignment => (
          selectedAssignment.assignmentCode === roleAssignment.assignmentCode
            && selectedAssignment.assignmentType === roleAssignment.assignmentType
        )))
      );

    const concatenatedUniqueCheckBoxValues = getConcatenatedUniqueCheckboxValues(
      foundSelectedAssignmentsInCheckedAssignments
    );

    const concatenatedRoleUniqueCheckBoxValues = getConcatenatedUniqueCheckboxValues(
      foundSelectedAssignmentsInCheckedRoleAssignments
    );

    const {
      value: assignmentCode,
    } = currentAssignmentsRowData.find(
      ({ value, assignmentTypeCode }) => selectedAssignments.find(selectedAssignment => (
        selectedAssignment.assignmentCode === value
          && selectedAssignment.assignmentType === assignmentTypeCode
      ))
    ) || {};

    const indeterminateValues = concatenatedUniqueCheckBoxValues.filter(value =>
      !foundSelectedAssignmentsInCheckedAssignments.every(assignment => assignment.privileges.includes(value))
        && foundSelectedAssignmentsInCheckedAssignments.some(assignment => assignment.privileges.includes(value))
    );

    const assignmentPermissionFilteredByType = userSettingsFormData.availableAssignmentPermissions;

    const assignmentPermissions = readOnly || userRole === userRoles.SUPER_USER
      ? assignmentPermissionFilteredByType
      : filterAssignmentPermissionsByPrivilege(
        loggedInUserCheckedAssignments,
        assignmentCode,
        assignmentPermissionFilteredByType
      );

    // checking dependant permissions
    const availableAssignmentPermissions = userSettingsFormData.availableAssignmentPermissions;
    const assignablePermissionsByValue = keyBy(getPermissionsFunction({
      permissions: availablePermissions,
      selectedRole,
      selectedClient,
    }), 'value');
    const missingDependencies = availableAssignmentPermissions.reduce((totalMissing, assignablePrivilege) => {
      const missing = getMissingDependencies(
        assignablePrivilege,
        selectedPermissionsFormValue,
        currentRoleAssignmentsMap.USR?.privileges);
      if (missing.length) {
        totalMissing[assignablePrivilege.value] = missing.length === 1
          ? `Required permission: "${assignablePermissionsByValue[missing[0]].label}" is not checked`
          : `Required permissions: ${missing.map(value => `"${assignablePermissionsByValue[value].label}"`).join(', ')} are not checked`;
      }
      return totalMissing;
    }, {});

    return (
      <div className="company-assignments">
        {readOnly ? null : <AssignmentsSelect
          currentAssignmentsRowData={currentAssignmentsRowData}
          checkedAssignmentsMap={checkedAssignmentsMap}
          selectAssignments={this.selectAssignments}
        />}

        <div className="row">
          <div className="col-sm-8">
            <AgGrid
              columnDefs={agGridConfigs[activePill].columnDefs}
              rowData={agGridConfigs[activePill].rowData}
              onGridReady={this.onGridReady}
              withSearchBar
              rowSelection={activePill === PillTypes.CURRENT_ASSIGNMENTS ? 'multiple' : 'single'}
              rowMultiSelectWithClick={!readOnly}
              onRowSelected={this.onRowSelected}
              suppressScrollOnNewData
              className="company-assignments-grid"
            />
          </div>
          <div className="col-sm-4 pt-4">
            {selectedAssignments.length > 1 ? (
              <div className="text-danger pb-4">
                {`Multiselection mode: ${selectedAssignments.length} assignments selected`}
              </div>
            ) : null}

            {!selectedAssignments.length || selectedAssignments.find(({ assignmentCode }) => assignmentCode === 'All')
              ? null
              : (
                <div className="permissions-panel">
                  <CheckboxGroup
                    className="d-flex flex-column"
                    checkboxDepth={2}
                    onChange={this.changeAssignmentsPermissions}
                    value={concatenatedUniqueCheckBoxValues}
                  >
                    {assignmentPermissions.map(({ label, value }) => {
                      const isChecked = concatenatedUniqueCheckBoxValues.includes(value);
                      const isFromRole = concatenatedRoleUniqueCheckBoxValues.includes(value);
                      return (
                        <label
                          key={value}
                          className={`d-flex align-items-baseline permission-item ${isChecked ? 'permission-checked' : ''}`}
                          data-tip={missingDependencies[value] ?? ''}
                        >
                          <Checkbox
                            className="mb-2"
                            value={value}
                            disabled={readOnly || !!missingDependencies[value]}
                            indeterminate={indeterminateValues.includes(value)}
                          />
                          <div className="dependency-indicator mr-1">
                            {!!missingDependencies[value] && <WarningIcon/> }
                          </div>
                          <div className="from-role-indicator mr-1">
                            {isFromRole ? '(Role)': ''}
                          </div>
                          {label}
                        </label>
                      );
                    })}
                  </CheckboxGroup>
                </div>
              )
            }
          </div>
        </div>
      </div>
    );
  }
}

export default withPillNavigation(
  () => PillsDefinition,
  {
    propTypes: {
      changeCompanyAssignmentsPill: PropTypes.func.isRequired,
      activeCompanyAssignmentsPill: PropTypes.string.isRequired,
      elementToRenderBeforePills: PropTypes.element.isRequired,
    },
    getActivePill: props => props.activeCompanyAssignmentsPill,
    onPillChangeEventPropKey: 'changeCompanyAssignmentsPill',
  }
)(CompanyAssignments);
